import {
  faEdit,
  faPlusCircle,
  faRetweet,
  faTrashAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Avatar, { AvatarGroup } from 'components/common/Avatar';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import Pagination from 'components/common/Pagination';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableWrapper, {
  IndeterminateCheckbox
} from 'components/common/advance-table/AdvanceTableWrapper';
import FaqBasicCard from 'components/pages/knowledge-center/faq/FaqBasicCard';
import { faqs } from 'data/faqs-subscription';
import draftEntryHelper from 'helpers/draft-entry-helper';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  Button,
  OverlayTrigger,
  ProgressBar,
  Spinner,
  Tooltip
} from 'react-bootstrap';
import SimpleBarReact from 'simplebar-react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { refreshUserMasterDetails } from 'stores/app';
import Colors from 'util/Colors';
import userHelper from 'helpers/userHelper';

const DarftLayout = ({
  draftEntry,
  createNewDraftEntry,
  removeDraft,
  removeMultipleDrafts,
  isDraftSynced,
  refreshDraftFromServer,
  isRefreshingDrafts,
  image,
  pageIndex,
  pageSize,
  totalRecords,
  endOfResults,
  onPageChange,
  onPageSizeChange,
  sortBy,
  onSortChange,
  isFetchingServerDrafts
}) => {
  const { userMaster } = useSelector(state => state.app);
  const dispatch = useDispatch();
  const [timezones, setTimezone] = useState(null);

  useEffect(() => {
    if (userMaster?.GUID) {
      dispatch(refreshUserMasterDetails(userMaster.GUID));
    }
  }, [dispatch, userMaster?.GUID]);
  useEffect(() => {
    if (userMaster?.LocalTimezone) {
      setTimezone(userHelper.getTimezoneObj(userMaster.LocalTimezone));
    }
  }, [userMaster?.LocalTimezone]);

  // ---- Multi-select for bulk delete -------------------------------------
  const [selectedIds, setSelectedIds] = useState([]);
  const visibleIds = draftEntry.map(d => d.id);
  const visibleIdsKey = visibleIds.join(',');

  // Drop any selections that are no longer on the page (page change / refresh).
  useEffect(() => {
    setSelectedIds(prev => prev.filter(id => visibleIds.includes(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleIdsKey]);

  const allSelected =
    visibleIds.length > 0 && selectedIds.length === visibleIds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => setSelectedIds(allSelected ? [] : visibleIds);
  const toggleOne = id =>
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  // -----------------------------------------------------------------------

  const columns = [
    {
      id: 'selection',
      disableSortBy: true,
      headerProps: { style: { width: 28 }, className: 'text-center' },
      cellProps: { style: { width: 28 }, className: 'text-center' },
      Header: () => (
        <IndeterminateCheckbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={toggleAll}
        />
      ),
      Cell: rowData => {
        const { id } = rowData.row.original;
        return (
          <IndeterminateCheckbox
            checked={selectedIds.includes(id)}
            onChange={() => toggleOne(id)}
          />
        );
      }
    },
    {
      accessor: 'title',
      Header: 'Last updated',
      sortType: (rowA, rowB) => {
        const parseDate = dateString => {
          // Convert the date string to a format that the Date constructor can parse
          const [datePart, timePart] = dateString.split(' ');
          const [month, day, year] = datePart.split('/').map(Number);
          const [hour, minute] = timePart.split(':').map(Number);
          const ampm = timePart.split(' ')[1];

          // Adjust hour for AM/PM
          const adjustedHour =
            ampm === 'PM' && hour !== 12
              ? hour + 12
              : ampm === 'AM' && hour === 12
              ? 0
              : hour;

          // Create a new Date object
          return new Date(year, month - 1, day, adjustedHour, minute);
        };

        const dateA = parseDate(rowA.original.title);
        const dateB = parseDate(rowB.original.title);
        // Compare the dates
        return dateA - dateB;
      },
      Cell: rowData => {
        //logger.log("rowData.row.original", rowData.row.original);
        const { id, title, isCompleted, source } = rowData.row.original;
        let timeInHST = title;
        if (timezones?.zone) {
          const parsedDate = moment.tz(title, 'MM/DD/YYYY hh:mm A', 'UTC');
          timeInHST = parsedDate
            .tz(timezones?.zone)
            .format('MM/DD/YYYY hh:mm A');
        }
        return (
          <Flex alignItems="center" className="position-relative">
            <div className="flex-1">
              <h6 className="mb-0 fw-semi-bold">
                <Link
                  className={
                    isCompleted
                      ? 'text-success stretched-link'
                      : 'text-dark stretched-link'
                  }
                  to={
                    '/notary/signing/' + draftEntryHelper.getDarftKeyFromId(id)
                  }
                >
                  {timeInHST}
                  &nbsp; {draftEntryHelper.getSourceIcon(source)}
                </Link>
              </h6>
              {isCompleted ? (
                <p className="fs--2 mb-0 fw-bold text-success">Completed</p>
              ) : null}
            </div>
          </Flex>
        );
      }
    },
    {
      accessor: 'step',
      Header: 'Step',
      cellProps: {
        className: 'fw-semi-bold fs--1'
      },
      Cell: rowData => {
        const { isCompleted, step } = rowData.row.original;
        return (
          <span className={isCompleted ? 'text-success' : ''}>
            {step || 'N/A'}
          </span>
        );
      }
    },
    {
      accessor: 'location',
      Header: 'Location',
      headerProps: {
        className: ''
      },
      cellProps: {
        className: ''
      },
      Cell: rowData => {
        const { id, location, isCompleted } = rowData.row.original;
        return (
          <h6 className="mb-0 fw-semi-bold">
            <Link
              className={isCompleted ? 'text-success ' : 'text-dark'}
              to={'/notary/signing/' + draftEntryHelper.getDarftKeyFromId(id)}
            >
              {location?.address_1 && location?.address_1?.length > 0
                ? location?.address_1
                : ''}
              {location?.address_2 && location?.address_2?.length > 0
                ? ', ' + location?.address_2
                : ''}
              {location?.city && location?.city?.length > 0
                ? ', ' + location?.city
                : ''}
              {location?.state && location?.state?.length > 0
                ? ', ' + location?.state
                : ''}
              {location?.zip && location?.zip?.length > 0
                ? '-' + location?.zip
                : ''}
            </Link>
          </h6>
        );
      }
    },
    {
      accessor: 'progress',
      Header: 'Progress',
      headerProps: {
        className: 'text-center'
      },
      cellProps: {
        className: 'text-center'
      },
      Cell: rowData => {
        const { isCompleted } = rowData.row.original;
        return (
          <ProgressBar
            now={rowData.row.original.progress}
            style={{ height: 5 }}
            className="rounded-pill align-middle"
            variant={isCompleted ? 'success' : 'progress-gradient'}
          />
        );
      }
    },
    {
      accessor: 'documents',
      Header: 'Docs',
      cellProps: {
        className: 'text-center fw-semi-bold fs--1'
      },
      headerProps: {
        className: 'text-center'
      },
      Cell: rowData => {
        const { isCompleted, documents } = rowData.row.original;
        return (
          <span className={isCompleted ? 'text-success' : ''}>{documents}</span>
        );
      }
    },
    {
      accessor: 'date',
      Header: 'Created',
      cellProps: {
        className: 'text-center fw-semi-bold fs--1'
      },
      headerProps: {
        className: 'text-center'
      },
      sortType: (rowA, rowB) => {
        const parseDate = dateString => {
          // Convert the date string to a format that the Date constructor can parse
          const [datePart, timePart] = dateString.split(' ');
          const [month, day, year] = datePart.split('/').map(Number);
          const [hour, minute] = timePart.split(':').map(Number);
          const ampm = timePart.split(' ')[1];

          // Adjust hour for AM/PM
          const adjustedHour =
            ampm === 'PM' && hour !== 12
              ? hour + 12
              : ampm === 'AM' && hour === 12
              ? 0
              : hour;

          // Create a new Date object
          return new Date(year, month - 1, day, adjustedHour, minute);
        };

        const dateA = parseDate(rowA.original.date);
        const dateB = parseDate(rowB.original.date);
        // Compare the dates
        return dateA - dateB;
      },
      Cell: rowData => {
        const { isCompleted, date } = rowData.row.original;
        let createdDate = date;
        if (timezones?.zone) {
          const parsedDate = moment.tz(date, 'MM/DD/YYYY hh:mm A', 'UTC');
          createdDate = parsedDate
            .tz(timezones?.zone)
            .format('MM/DD/YYYY hh:mm A');
        }
        return (
          <span className={isCompleted ? 'text-success' : ''}>
            {createdDate}
          </span>
        );
      }
    },
    {
      accessor: 'members',
      Header: 'Signers',
      disableSortBy: true,
      headerProps: {
        className: 'text-center'
      },
      Cell: rowData => {
        const { isCompleted } = rowData.row.original;
        return rowData.row.original.members.length ? (
          <AvatarGroup className="justify-content-start">
            {rowData.row.original.members.map(({ name, id }) => {
              return (
                <OverlayTrigger
                  overlay={<Tooltip id={'tooltip-' + id}>{name}</Tooltip>}
                  key={id}
                  placement="top"
                >
                  <span>
                    <Avatar
                      key={id}
                      name={name}
                      title={name}
                      className="border border-3 rounded-circle border-light"
                      mediaClass={isCompleted ? 'bg-success' : ''}
                    />
                  </span>
                </OverlayTrigger>
              );
            })}
          </AvatarGroup>
        ) : (
          <Flex
            justifyContent="start"
            className="mx-1 fw-bold text-danger fs-0"
          >
            N/A
          </Flex>
        );
      }
    },
    {
      accessor: 'action',
      Header: 'Actions',
      disableSortBy: true,
      cellProps: {
        className: 'text-start'
      },
      Cell: rowData => {
        //logger.log(rowData.row.original);
        const { id, isCompleted } = rowData.row.original;
        return (
          <Flex justifyContent="start" alignContent="start">
            {/* {isCompleted && (
              <div className="text-right cursor-pointer">
                <Button
                  variant="outline-success"
                  onClick={() => uploadFinishedDraft(id)}
                  size="sm"
                >
                  {' '}
                  <FontAwesomeIcon icon={faUpload} /> Upload
                </Button>
              </div>
            )} */}
            <div className="text-start cursor-pointer mx-1">
              <Button
                variant="outline-danger"
                onClick={() => removeDraft(id)}
                size="sm"
              >
                {' '}
                <FontAwesomeIcon icon={faTrashAlt} />
              </Button>
            </div>
          </Flex>
        );
      }
    }
  ];
  const AddNewSigning = () => {
    return (
      <div className="text-center py-4">
        <h4>
          <FontAwesomeIcon
            color={Colors.gray.lightColor}
            size="5x"
            icon={faEdit}
          />
        </h4>
        <p className="my-3">You don't have any draft entries.</p>
        <p className="text-center">
          <Button
            onClick={() => refreshDraftFromServer()}
            className="mx-1"
            variant="success"
          >
            {isRefreshingDrafts ? (
              <Flex justifyContent={'center'} alignItems="center">
                <Spinner animation="border" size="sm" /> &nbsp; Refreshing...
              </Flex>
            ) : (
              <>
                <FontAwesomeIcon icon={faRetweet} /> Refresh
              </>
            )}
          </Button>
        </p>
      </div>
    );
  };

  const renderBody = () => {
    if (!isDraftSynced) {
      return (
        <Flex
          alignItems={'center'}
          justifyContent="center"
          direction={'column'}
          className="py-4"
        >
          <Spinner animation="border" /> <br />
          <span>Syncing draft. Please wait...</span>
        </Flex>
      );
    }

    if (draftEntry.length === 0) {
      return <AddNewSigning />;
    }

    return (
      <div className={isFetchingServerDrafts ? 'refreshing-skeleton' : ''}>
        <AdvanceTableWrapper
          columns={columns}
          data={draftEntry}
          sortable={false}
          perPage={pageSize}
          sortBy={sortBy}
          onSortChange={onSortChange}
        >
          <Pagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalRecords={totalRecords}
            currentCount={draftEntry.length}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            endOfResults={endOfResults}
            disabled={isRefreshingDrafts}
            className="px-0"
            onRefresh={() => refreshDraftFromServer()}
            loading={isRefreshingDrafts || isFetchingServerDrafts}
          />
          <SimpleBarReact>
            <AdvanceTable
              table
              headerClassName="bg-light text-800 text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                borderless: true,
                className: `fs--2 mb-0 overflow-hidden${
                  isRefreshingDrafts ? ' refreshing-skeleton' : ''
                }`
              }}
            />
          </SimpleBarReact>
        </AdvanceTableWrapper>
      </div>
    );
  };

  return (
    <>
      <FalconComponentCard noGuttersBottom>
        <FalconComponentCard.Header
          image={image}
          className={'px-4'}
          title="Notarization Signing Drafts"
          subtitle="Browse your saved notarization drafts"
          endEl={
            <Flex alignItems="center">
              {selectedIds.length > 0 && (
                <Button
                  onClick={() =>
                    removeMultipleDrafts &&
                    removeMultipleDrafts(selectedIds, () => setSelectedIds([]))
                  }
                  className="mx-1"
                  variant="danger"
                >
                  <FontAwesomeIcon icon={faTrashAlt} /> Delete Multiple (
                  {selectedIds.length})
                </Button>
              )}
              <Button
                onClick={createNewDraftEntry}
                className="mx-1"
                variant="primary"
              >
                <FontAwesomeIcon icon={faPlusCircle} /> Start New
              </Button>
            </Flex>
          }
        />
        <FalconComponentCard.Body noLight className="py-0 px-4">
          {renderBody()}
        </FalconComponentCard.Body>
      </FalconComponentCard>
      <div className="mt-3">
        <FaqBasicCard
          faqs={faqs}
          header
          headerText="Frequently asked questions"
          bodyClass="bg-light"
          faqIdentity="Draft page"
        />
      </div>
    </>
  );
};

DarftLayout.propTypes = {
  draftEntry: PropTypes.array.isRequired,
  createNewDraftEntry: PropTypes.func.isRequired,
  removeDraft: PropTypes.func,
  removeMultipleDrafts: PropTypes.func,
  isDraftSynced: PropTypes.bool,
  refreshDraftFromServer: PropTypes.func,
  isRefreshingDrafts: PropTypes.bool,
  image: PropTypes.string,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  totalRecords: PropTypes.number,
  endOfResults: PropTypes.bool,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  sortBy: PropTypes.array,
  onSortChange: PropTypes.func
};

export default DarftLayout;
